import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import RemoveOutlinedIcon from "@mui/icons-material/RemoveOutlined";
import { DataGrid } from "@mui/x-data-grid";
import AceEditor from "react-ace";
import "ace-builds/src-noconflict/mode-json";
import "ace-builds/src-noconflict/theme-github";
import "ace-builds/src-noconflict/theme-monokai";
import MarkdownContent from "../MarkdownContent";
import useAgentStore from "../useAgentStore";
import CONFIG from "../../../config";
import { StyledSnackbar } from "../../common/AppSnackbar";

const ACE_MONOSPACE_FONT_FAMILY =
  '"Monaco", "Menlo", "Ubuntu Mono", "Droid Sans Mono", "Consolas", monospace';

function inferMimeTypeFromFileName(fileName) {
  if (typeof fileName !== "string") return "";
  const lower = fileName.toLowerCase();
  if (lower.endsWith(".md")) return "text/markdown";
  if (lower.endsWith(".txt")) return "text/plain";
  if (lower.endsWith(".json")) return "application/json";
  if (lower.endsWith(".csv")) return "text/csv";
  return "";
}

function resolveDownloadUrl(fileContext) {
  if (typeof fileContext?.downloadUrl === "string" && fileContext.downloadUrl) {
    return fileContext.downloadUrl;
  }
  if (typeof fileContext?.fileId === "string" && fileContext.fileId) {
    return `${CONFIG.FILE_SERVICE.QUERY_URL}/${fileContext.fileId}`;
  }
  return "";
}

function isEditableTextMime(mimeType) {
  if (!mimeType) return false;
  const normalized = String(mimeType).toLowerCase();
  return (
    normalized.startsWith("text/") ||
    normalized.includes("json") ||
    normalized.includes("csv") ||
    normalized.includes("markdown")
  );
}

function parseCsv(text) {
  const input = String(text ?? "");
  if (!input) return [];

  const rows = [];
  let row = [];
  let cell = "";
  let inQuotes = false;

  for (let i = 0; i < input.length; i += 1) {
    const ch = input[i];

    if (inQuotes) {
      if (ch === '"') {
        if (input[i + 1] === '"') {
          cell += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        cell += ch;
      }
      continue;
    }

    if (ch === '"') {
      inQuotes = true;
      continue;
    }

    if (ch === ",") {
      row.push(cell);
      cell = "";
      continue;
    }

    if (ch === "\n" || ch === "\r") {
      row.push(cell);
      cell = "";
      rows.push(row);
      row = [];
      if (ch === "\r" && input[i + 1] === "\n") {
        i += 1;
      }
      continue;
    }

    cell += ch;
  }

  row.push(cell);
  const endedWithNewline = /[\r\n]$/.test(input);
  const trailingEmptyRow = row.length === 1 && row[0] === "";
  if (!(endedWithNewline && trailingEmptyRow)) {
    rows.push(row);
  }
  return rows;
}

function normalizeCsvRows(rows, minimumCols = 1) {
  const inputRows = Array.isArray(rows) ? rows : [];
  const maxCols = Math.max(
    minimumCols,
    ...inputRows.map((row) => (Array.isArray(row) ? row.length : 0))
  );
  if (!inputRows.length) {
    return [Array.from({ length: maxCols }, () => "")];
  }
  return inputRows.map((row) => {
    const safeRow = Array.isArray(row) ? [...row] : [];
    while (safeRow.length < maxCols) {
      safeRow.push("");
    }
    return safeRow;
  });
}

function escapeCsvCell(value) {
  const raw = String(value ?? "");
  if (/[",\n\r]/.test(raw)) {
    return `"${raw.replace(/"/g, '""')}"`;
  }
  return raw;
}

function serializeCsv(rows) {
  const normalized = normalizeCsvRows(rows);
  return normalized
    .map((row) => row.map((cell) => escapeCsvCell(cell)).join(","))
    .join("\n");
}

function buildCsvGridColumns(headerRow, editable = false) {
  const normalizedHeader = Array.isArray(headerRow) ? headerRow : [""];
  return normalizedHeader.map((headerCell, colIdx) => ({
    field: `col_${colIdx}`,
    headerName: String(headerCell || `Column ${colIdx + 1}`),
    editable,
    flex: 1,
    minWidth: 140,
    sortable: false,
  }));
}

function buildCsvGridRows(allRows) {
  const normalized = normalizeCsvRows(allRows);
  const dataRows = normalized.slice(1);
  return dataRows.map((row, rowIdx) => {
    const record = { id: rowIdx };
    row.forEach((value, colIdx) => {
      record[`col_${colIdx}`] = value ?? "";
    });
    return record;
  });
}

export default function AgentFileEditor({
  fileContext,
  refreshTrigger = 0,
  onSaved = null,
}) {
  const theme = useTheme();
  const aceTheme = theme.palette.mode === "dark" ? "monokai" : "github";
  const [content, setContent] = useState("");
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [warning, setWarning] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [csvDraftRows, setCsvDraftRows] = useState([[""]]);
  const jsonEditAceRef = useRef(null);
  const jsonViewAceRef = useRef(null);
  const aceTimerIdsRef = useRef([]);

  const fileName = fileContext?.fileName || "Untitled file";
  const mimeType =
    fileContext?.mimeType || inferMimeTypeFromFileName(fileName) || "";
  const downloadUrl = resolveDownloadUrl(fileContext);
  const isMarkdown = /markdown/i.test(mimeType) || /\.md$/i.test(fileName);
  const isJson = /json/i.test(mimeType) || /\.json$/i.test(fileName);
  const isCsv = /csv/i.test(mimeType) || /\.csv$/i.test(fileName);
  const canEdit = isEditableTextMime(mimeType) || /\.(md|txt|json|csv)$/i.test(fileName);

  const renderedContent = useMemo(() => {
    if (!isJson || isEditing) return content;
    try {
      return JSON.stringify(JSON.parse(content), null, 2);
    } catch {
      return content;
    }
  }, [content, isEditing, isJson]);

  const csvViewRows = useMemo(() => {
    if (!isCsv) return [];
    const parsed = parseCsv(content);
    return normalizeCsvRows(parsed);
  }, [content, isCsv]);

  const normalizedCsvDraftRows = useMemo(
    () => normalizeCsvRows(csvDraftRows),
    [csvDraftRows]
  );
  const csvHeaderRow = normalizedCsvDraftRows[0] || [""];
  const csvGridRows = useMemo(
    () => buildCsvGridRows(normalizedCsvDraftRows),
    [normalizedCsvDraftRows]
  );
  const csvViewColumns = useMemo(
    () => buildCsvGridColumns(csvViewRows[0] || [""], false),
    [csvViewRows]
  );
  const csvEditColumns = useMemo(
    () => buildCsvGridColumns(csvHeaderRow, true),
    [csvHeaderRow]
  );

  const loadFileContent = useCallback(async () => {
    if (!downloadUrl) {
      setContent("");
      setDraft("");
      setError("No download URL available for this file.");
      return;
    }
    setLoading(true);
    setError("");
    setSuccess("");
    setWarning("");
    try {
      const response = await fetch(downloadUrl, { method: "GET" });
      if (!response.ok) {
        throw new Error(`Failed to load file (${response.status})`);
      }
      const text = await response.text();
      setContent(text);
      setDraft(text);
      if (isCsv) {
        setCsvDraftRows(normalizeCsvRows(parseCsv(text)));
      }
    } catch (e) {
      setError(e?.message || "Failed to load file content");
      setContent("");
      setDraft("");
      setCsvDraftRows([[""]]);
    } finally {
      setLoading(false);
    }
  }, [downloadUrl, isCsv]);

  useEffect(() => {
    setIsEditing(false);
    loadFileContent();
  }, [loadFileContent, refreshTrigger, fileContext?.fileId]);

  const handleDownload = useCallback(() => {
    if (!downloadUrl) return;
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = fileName || "file";
    link.rel = "noopener noreferrer";
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [downloadUrl, fileName]);

  const syncAceMetrics = useCallback((editor) => {
    if (!editor) return;
    try {
      // Keep text metrics in sync with rendered glyphs after mount/toggle.
      editor.setOption("fontFamily", "monospace");
      editor.setFontSize("12px");
      editor.renderer.$textLayer?.checkForSizeChanges?.();
      editor.renderer.updateFontSize();
      editor.renderer.updateFull();
      editor.resize(true);
    } catch {
      // no-op
    }
  }, []);

  const registerJsonEditAce = useCallback(
    (editor) => {
      jsonEditAceRef.current = editor;
      syncAceMetrics(editor);

      const t1 = setTimeout(() => syncAceMetrics(editor), 0);
      const t2 = setTimeout(() => syncAceMetrics(editor), 80);
      const t3 = setTimeout(() => syncAceMetrics(editor), 220);
      aceTimerIdsRef.current.push(t1, t2, t3);
    },
    [syncAceMetrics]
  );

  const registerJsonViewAce = useCallback(
    (editor) => {
      jsonViewAceRef.current = editor;
      syncAceMetrics(editor);

      const t1 = setTimeout(() => syncAceMetrics(editor), 0);
      const t2 = setTimeout(() => syncAceMetrics(editor), 80);
      const t3 = setTimeout(() => syncAceMetrics(editor), 220);
      aceTimerIdsRef.current.push(t1, t2, t3);
    },
    [syncAceMetrics]
  );

  useEffect(() => {
    return () => {
      aceTimerIdsRef.current.forEach((id) => clearTimeout(id));
      aceTimerIdsRef.current = [];
    };
  }, []);

  useEffect(() => {
    if (!isJson || loading) return;
    const activeAce = isEditing ? jsonEditAceRef.current : jsonViewAceRef.current;
    if (!activeAce) return;
    syncAceMetrics(activeAce);
  }, [fileContext?.fileId, isEditing, isJson, loading, syncAceMetrics]);

  const handleSave = useCallback(async () => {
    if (!fileContext?.agentId) {
      setError("Unable to save: missing agent ID in file context.");
      return;
    }
    if (!fileContext?.fileId) {
      setError("Unable to save: missing file ID in file context.");
      return;
    }

    const saveContent = isCsv ? serializeCsv(csvDraftRows) : draft;
    setSaving(true);
    setError("");
    setSuccess("");
    setWarning("");
    try {
      const savedArtifact = await useAgentStore.getState().saveEditedAgentFile({
        agentId: fileContext.agentId,
        previousFileId: fileContext.fileId,
        fileName,
        content: saveContent,
        mimeType,
      });

      const updatedContext = {
        ...fileContext,
        ...savedArtifact,
        fileName: savedArtifact?.fileName || fileName,
        mimeType: savedArtifact?.mimeType || mimeType,
      };

      setContent(saveContent);
      setDraft(saveContent);
      if (isCsv) {
        setCsvDraftRows(normalizeCsvRows(parseCsv(saveContent)));
      }
      setIsEditing(false);
      setSuccess("File saved.");
      setWarning(savedArtifact?.warning || "");

      if (typeof onSaved === "function") {
        onSaved(updatedContext);
      }
    } catch (e) {
      setError(e?.message || "Failed to save file");
    } finally {
      setSaving(false);
    }
  }, [csvDraftRows, draft, fileContext, fileName, isCsv, mimeType, onSaved]);

  const handleCsvRowUpdate = useCallback(
    (newRow) => {
      const rowIndex = Number(newRow?.id);
      if (!Number.isFinite(rowIndex) || rowIndex < 0) {
        return newRow;
      }

      setCsvDraftRows((prev) => {
        const next = normalizeCsvRows(prev).map((row) => [...row]);
        const colCount = next[0]?.length || 1;
        const targetRowIndex = rowIndex + 1;

        while (next.length <= targetRowIndex) {
          next.push(Array.from({ length: colCount }, () => ""));
        }

        for (let colIdx = 0; colIdx < colCount; colIdx += 1) {
          next[targetRowIndex][colIdx] = String(
            newRow?.[`col_${colIdx}`] ?? ""
          );
        }
        return next;
      });

      return newRow;
    },
    [setCsvDraftRows]
  );

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: 140,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1.5,
          color: "text.secondary",
        }}
      >
        <CircularProgress size={22} />
        <Typography sx={{ fontSize: "12px" }}>Loading file content…</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, minHeight: 0 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <Typography sx={{ fontSize: "12px", color: "text.secondary" }}>
          {mimeType || "unknown type"}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {downloadUrl && (
            <Button
              size="small"
              variant="outlined"
              onClick={handleDownload}
              startIcon={<DownloadOutlinedIcon sx={{ fontSize: 13 }} />}
              sx={{ textTransform: "none", fontSize: "11px", minWidth: 0 }}
            >
              Download
            </Button>
          )}
          {canEdit && !isEditing && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                if (isCsv) {
                  setCsvDraftRows(normalizeCsvRows(parseCsv(draft)));
                }
                setIsEditing(true);
              }}
              sx={{ textTransform: "none", fontSize: "11px", minWidth: 0 }}
            >
              Edit
            </Button>
          )}
          {canEdit && isEditing && (
            <>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  setDraft(content);
                  if (isCsv) {
                    setCsvDraftRows(normalizeCsvRows(parseCsv(content)));
                  }
                  setIsEditing(false);
                }}
                disabled={saving}
                sx={{ textTransform: "none", fontSize: "11px", minWidth: 0 }}
              >
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={handleSave}
                disabled={saving}
                sx={{ textTransform: "none", fontSize: "11px", minWidth: 0 }}
              >
                {saving ? "Saving..." : "Save"}
              </Button>
            </>
          )}
        </Box>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}
      {warning && <Alert severity="warning">{warning}</Alert>}
      <StyledSnackbar
        open={Boolean(success)}
        message={success}
        severity="success"
        onClose={() => setSuccess("")}
      />

      {!canEdit && (
        <Alert severity="info">
          This file type is not editable in chat. Use <strong>Download</strong> to
          view it locally.
        </Alert>
      )}

      {canEdit && isEditing && (
        <>
          {isCsv ? (
            <Box
              sx={{
                border: 1,
                borderColor: "divider",
                borderRadius: "8px",
                bgcolor: "background.paper",
                p: 1.5,
                display: "flex",
                flexDirection: "column",
                gap: 1,
                minHeight: 180,
                maxHeight: "68vh",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddOutlinedIcon sx={{ fontSize: 12 }} />}
                  onClick={() => {
                    setCsvDraftRows((prev) => {
                      const normalized = normalizeCsvRows(prev);
                      const columnCount = normalized[0]?.length || 1;
                      return [...normalized, Array.from({ length: columnCount }, () => "")];
                    });
                  }}
                  disabled={saving}
                  sx={{ textTransform: "none", fontSize: "11px", minWidth: 0 }}
                >
                  Row
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddOutlinedIcon sx={{ fontSize: 12 }} />}
                  onClick={() => {
                    setCsvDraftRows((prev) =>
                      normalizeCsvRows(prev).map((row) => [...row, ""])
                    );
                  }}
                  disabled={saving}
                  sx={{ textTransform: "none", fontSize: "11px", minWidth: 0 }}
                >
                  Column
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<RemoveOutlinedIcon sx={{ fontSize: 12 }} />}
                  onClick={() => {
                    setCsvDraftRows((prev) => {
                      const normalized = normalizeCsvRows(prev);
                      if (normalized.length <= 1) return normalized;
                      return normalized.slice(0, -1);
                    });
                  }}
                  disabled={saving}
                  sx={{ textTransform: "none", fontSize: "11px", minWidth: 0 }}
                >
                  Row
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<RemoveOutlinedIcon sx={{ fontSize: 12 }} />}
                  onClick={() => {
                    setCsvDraftRows((prev) => {
                      const normalized = normalizeCsvRows(prev);
                      if ((normalized[0]?.length || 0) <= 1) return normalized;
                      return normalized.map((row) => row.slice(0, -1));
                    });
                  }}
                  disabled={saving}
                  sx={{ textTransform: "none", fontSize: "11px", minWidth: 0 }}
                >
                  Column
                </Button>
              </Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: `repeat(${csvHeaderRow.length || 1}, minmax(140px, 1fr))`,
                  gap: 1,
                }}
              >
                {csvHeaderRow.map((headerValue, colIdx) => (
                  <TextField
                    key={`csv-header-edit-${colIdx}`}
                    value={headerValue}
                    onChange={(event) => {
                      const value = event.target.value;
                      setCsvDraftRows((prev) => {
                        const next = normalizeCsvRows(prev).map((row) => [...row]);
                        next[0][colIdx] = value;
                        return next;
                      });
                    }}
                    variant="outlined"
                    size="small"
                    label={`Header ${colIdx + 1}`}
                    disabled={saving}
                    inputProps={{ style: { fontSize: 12, fontWeight: 600 } }}
                  />
                ))}
              </Box>
              <Box sx={{ height: "100%", minHeight: 240, maxHeight: "52vh", width: "100%" }}>
                <DataGrid
                  rows={csvGridRows}
                  columns={csvEditColumns}
                  disableRowSelectionOnClick
                  hideFooter
                  editMode="cell"
                  processRowUpdate={handleCsvRowUpdate}
                  onProcessRowUpdateError={(editError) => {
                    setError(editError?.message || "Failed to apply CSV cell edit");
                  }}
                  sx={{
                    border: 1,
                    borderColor: "divider",
                    borderRadius: "6px",
                    "& .MuiDataGrid-columnHeader": {
                      bgcolor: "background.subtle",
                    },
                    "& .MuiDataGrid-cell": {
                      fontSize: "12px",
                    },
                  }}
                  slots={{
                    noRowsOverlay: () => (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          height: "100%",
                          color: "text.secondary",
                          fontSize: "12px",
                        }}
                      >
                        No data rows. Add a row to start editing.
                      </Box>
                    ),
                  }}
                />
              </Box>
            </Box>
          ) : (
            <>
              {isJson ? (
                <Box
                  sx={{
                    border: 1,
                    borderColor: "divider",
                    borderRadius: "8px",
                    overflow: "hidden",
                    bgcolor: "background.paper",
                    "& .ace_editor, & .ace_editor *": {
                      fontFamily: `${ACE_MONOSPACE_FONT_FAMILY} !important`,
                      fontSize: "12px !important",
                      fontWeight: "400 !important",
                      textRendering: "auto !important",
                      fontVariantLigatures: "none !important",
                      fontFeatureSettings: '"liga" 0, "calt" 0 !important',
                      letterSpacing: "0 !important",
                      wordSpacing: "0 !important",
                      textIndent: "0 !important",
                    },
                  }}
                >
                  <AceEditor
                    mode="json"
                    theme={aceTheme}
                    name={`agent-file-json-editor-${fileContext?.fileId || "new"}`}
                    onLoad={registerJsonEditAce}
                    editorProps={{ $blockScrolling: true }}
                    value={draft}
                    onChange={(value) => setDraft(value)}
                    readOnly={saving}
                    width="100%"
                    height="52vh"
                    fontSize={12}
                    wrapEnabled
                    setOptions={{
                      showPrintMargin: false,
                      tabSize: 2,
                      useSoftTabs: true,
                      fontFamily: "monospace",
                      fontSize: "12px",
                    }}
                  />
                </Box>
              ) : (
                <TextField
                  value={draft}
                  onChange={(event) => setDraft(event.target.value)}
                  multiline
                  minRows={14}
                  maxRows={28}
                  fullWidth
                  disabled={saving}
                  sx={{
                    "& .MuiInputBase-root": {
                      fontFamily: "monospace",
                      fontSize: "12px",
                      lineHeight: 1.5,
                      bgcolor: "background.paper",
                    },
                  }}
                />
              )}
            </>
          )}
        </>
      )}

      {canEdit && !isEditing && (
        <Box
          sx={{
            border: 1,
            borderColor: "divider",
            borderRadius: "8px",
            bgcolor: "background.paper",
            p: 1.5,
            minHeight: 140,
            maxHeight: "68vh",
            overflow: "auto",
          }}
        >
          {isCsv ? (
            <Box sx={{ height: "56vh", minHeight: 240, width: "100%" }}>
              <DataGrid
                rows={buildCsvGridRows(csvViewRows)}
                columns={csvViewColumns}
                disableRowSelectionOnClick
                pageSizeOptions={[10, 25, 50]}
                initialState={{
                  pagination: { paginationModel: { pageSize: 25 } },
                }}
                sx={{
                  border: 1,
                  borderColor: "divider",
                  borderRadius: "6px",
                  "& .MuiDataGrid-columnHeader": {
                    bgcolor: "background.subtle",
                  },
                  "& .MuiDataGrid-cell": {
                    fontSize: "12px",
                  },
                }}
                slots={{
                  noRowsOverlay: () => (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                        color: "text.secondary",
                        fontSize: "12px",
                      }}
                    >
                      No data rows in this CSV.
                    </Box>
                  ),
                }}
              />
            </Box>
          ) : isMarkdown ? (
            <MarkdownContent>{renderedContent || "_Empty file_"}</MarkdownContent>
          ) : isJson ? (
            <Box
              sx={{
                border: 1,
                borderColor: "divider",
                borderRadius: "8px",
                overflow: "hidden",
                bgcolor: "background.paper",
                "& .ace_editor, & .ace_editor *": {
                  fontFamily: `${ACE_MONOSPACE_FONT_FAMILY} !important`,
                  fontSize: "12px !important",
                  fontWeight: "400 !important",
                  textRendering: "auto !important",
                  fontVariantLigatures: "none !important",
                  fontFeatureSettings: '"liga" 0, "calt" 0 !important',
                  letterSpacing: "0 !important",
                  wordSpacing: "0 !important",
                  textIndent: "0 !important",
                },
              }}
            >
              <AceEditor
                mode="json"
                theme={aceTheme}
                name={`agent-file-json-viewer-${fileContext?.fileId || "new"}`}
                onLoad={registerJsonViewAce}
                editorProps={{ $blockScrolling: true }}
                value={renderedContent || "{}"}
                readOnly
                width="100%"
                height="56vh"
                fontSize={12}
                wrapEnabled
                setOptions={{
                  showPrintMargin: false,
                  tabSize: 2,
                  useSoftTabs: true,
                  fontFamily: "monospace",
                  fontSize: "12px",
                }}
              />
            </Box>
          ) : (
            <Typography
              component="pre"
              sx={{
                fontSize: "12px",
                fontFamily: "monospace",
                color: "text.primary",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                m: 0,
                lineHeight: 1.5,
              }}
            >
              {renderedContent || "File is empty."}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}
